import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { usePermission } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';

/**
 * A route component that renders its children only if the user has the required permission(s)
 * Enhanced with an improved system to prevent redirects during form submissions
 * @param {Object} props - Component props
 * @param {string|string[]} props.requiredPermission - Required permission(s) for accessing the route
 * @param {boolean} props.requireAll - If true, user must have all permissions; if false, any one permission is sufficient
 * @param {string} props.redirectTo - Where to redirect if permission is denied (defaults to /login)
 * @param {boolean} props.softCheck - If true, shows an error message instead of redirecting
 */
const PermissionRoute = ({ 
  requiredPermission, 
  requireAll = false, 
  redirectTo = '/login',
  softCheck = false
}) => {
  const { currentUser, loading: authLoading } = useAuth();
  const { hasPermission, hasAllPermissions, hasAnyPermission, loading: permissionLoading } = usePermission();
  const [redirectPaused, setRedirectPaused] = useState(false);
  const [submissionTimeout, setSubmissionTimeout] = useState(null);
  const location = useLocation();

  // Setup a global form submission state variable
  useEffect(() => {
    // Initialize the global submission state if it doesn't exist
    if (typeof window.__formSubmissionActive === 'undefined') {
      window.__formSubmissionActive = false;
    }
  }, []);

  // Listen for form submission state changes
  useEffect(() => {
    // Handle form submission state events
    const handleSubmissionState = (event) => {
      if (event.detail && typeof event.detail.isSubmitting === 'boolean') {
        const isSubmitting = event.detail.isSubmitting;
        
        // Set the global submission state
        window.__formSubmissionActive = isSubmitting;
        
        // Update local state
        setRedirectPaused(isSubmitting);
        
        // Clear any existing timeout
        if (submissionTimeout) {
          clearTimeout(submissionTimeout);
          setSubmissionTimeout(null);
        }
        
        // If submission is starting, set a safety timeout to eventually release the pause
        // This prevents the application from getting stuck if a form submission fails to complete
        if (isSubmitting) {
          const timeout = setTimeout(() => {
            console.log('PermissionRoute: Safety timeout triggered - releasing redirect pause');
            window.__formSubmissionActive = false;
            setRedirectPaused(false);
            setSubmissionTimeout(null);
          }, 30000); // 30 seconds should be enough for most form submissions
          
          setSubmissionTimeout(timeout);
        }
        
        console.log(`PermissionRoute: Form submission state changed to ${isSubmitting ? 'active' : 'inactive'}`);
      }
    };

    // Listen for both the custom event and direct property changes
    window.addEventListener('formSubmissionState', handleSubmissionState);
    
    return () => {
      // Cleanup listeners and timeout
      window.removeEventListener('formSubmissionState', handleSubmissionState);
      if (submissionTimeout) {
        clearTimeout(submissionTimeout);
      }
    };
  }, [submissionTimeout]);
  
  // Check for refresh indicator in URL hash or session storage
  // This is more reliable than just checking session storage
  const isFromPageRefresh = !!(
    sessionStorage.getItem('fromPageRefresh') === 'true' || 
    (typeof window !== 'undefined' && window.performance && 
     window.performance.navigation && 
     window.performance.navigation.type === 1)
  );
  
  // Always record if this is an LMS path to help with detection
  useEffect(() => {
    const isLMSPath = location.pathname.includes('/labor_management_system');
    if (isLMSPath) {
      console.log('PermissionRoute: LMS path detected, setting lmsPathViewed flag');
      sessionStorage.setItem('lmsPathViewed', 'true');
    }
  }, [location.pathname]);
  
  // Get information about stored routes
  const lastAttemptedRoute = sessionStorage.getItem('lastAttemptedProtectedRoute');
  const wasOnLMS = sessionStorage.getItem('lmsPathViewed') === 'true';
  const hasStoredRoute = !!lastAttemptedRoute;
  const matchesStoredRoute = lastAttemptedRoute === location.pathname;
  
  // Check if this appears to be a refresh on a protected route
  const likelyRefreshOnProtectedRoute = 
    isFromPageRefresh && 
    (matchesStoredRoute || 
     (location.pathname.includes('/labor_management_system') && wasOnLMS));
  
  console.log('PermissionRoute: Refresh state check:', {
    isFromPageRefresh,
    lastAttemptedRoute,
    currentPath: location.pathname,
    matchesStoredRoute,
    wasOnLMS,
    likelyRefreshOnProtectedRoute
  });
  
  // If still loading authentication or permissions, show loading indicator
  // This prevents premature permission checks before data is loaded
  if (authLoading || permissionLoading) {
    console.log('PermissionRoute: Still loading auth or permissions, showing loading indicator');
    
    if (likelyRefreshOnProtectedRoute) {
      console.log('PermissionRoute: Route restoration in progress, allowing through while loading permissions');
      // CRITICAL - Allow the route to render while permissions are still loading during refresh
      return <Outlet />;
    }
    
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <div className="text-slate-500">Loading permissions...</div>
        </div>
      </div>
    );
  }
  
  // Check if the user is authenticated
  if (!currentUser) {
    console.log('PermissionRoute: User not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  // Get path from session storage to prevent redirect loops
  const storedPath = sessionStorage.getItem('lastAttemptedProtectedRoute');
  const isRestoringPath = storedPath && storedPath === location.pathname;
  
  // Store current protected route path to restore after permission loading
  if (!isRestoringPath && location.pathname !== '/') {
    sessionStorage.setItem('lastAttemptedProtectedRoute', location.pathname);
    console.log('PermissionRoute: Stored protected route path:', location.pathname);
  }
  
  // Check permissions - only performed after permissions are fully loaded
  console.log('PermissionRoute: Checking permissions for path:', location.pathname);
  let hasRequiredPermission = false;
  
  if (Array.isArray(requiredPermission)) {
    hasRequiredPermission = requireAll 
      ? hasAllPermissions(requiredPermission)
      : hasAnyPermission(requiredPermission);
  } else {
    hasRequiredPermission = hasPermission(requiredPermission);
  }
  
  // Special case for truck scheduler permissions
  if (!hasRequiredPermission && 
      (location.pathname.includes('/truck_scheduler/inbound') || 
       location.pathname.includes('/truck_scheduler/outbound'))) {
    
    // Standardized on single truck_scheduler permission for both inbound and outbound
    const isTruckScheduler = hasPermission('truck_scheduler');
    
    if (isTruckScheduler) {
      hasRequiredPermission = true; // truck_scheduler grants access to both inbound and outbound
    }
  }
  
  // Log detailed permission status for debugging
  console.log('PermissionRoute: Permission check result:', {
    path: location.pathname,
    requiredPermission,
    hasPermission: hasRequiredPermission,
    permissionLoading
  });
  
  // If the user doesn't have the required permissions
  if (!hasRequiredPermission) {
    // Check global submission state first (more reliable than the local state)
    const globalSubmissionActive = window.__formSubmissionActive === true;
    
    // For LMS routes specifically, let's be even more careful about redirects
    const isLMSPath = location.pathname.includes('/labor_management_system');
    const wasViewingLMS = sessionStorage.getItem('lmsPathViewed') === 'true';
    
    // Special flag for allowing LMS access during debug
    if (isLMSPath && wasViewingLMS && sessionStorage.getItem('permissions_still_loading') === 'true') {
      console.log('PermissionRoute: LMS permissions appear to be loading, allowing access temporarily');
      // Don't redirect - permissions might just be delayed
      return <Outlet />;
    }

    // If we just loaded the page (detected by window.performance), we should be lenient
    const isPageLoad = typeof window !== 'undefined' && 
      window.performance && 
      window.performance.timing && 
      (Date.now() - window.performance.timing.navigationStart < 3000);
    
    // For any refresh-like scenario, we want to be more permissive
    const isRefreshLikeScenario = 
      likelyRefreshOnProtectedRoute || 
      isPageLoad || 
      sessionStorage.getItem('fromPageRefresh') === 'true';
    
    // During a refresh or form submission, we want to be more lenient with permissions
    if (globalSubmissionActive || redirectPaused || softCheck || isRefreshLikeScenario) {
      console.log('PermissionRoute: Redirect paused due to:', {
        activeSubmission: globalSubmissionActive || redirectPaused,
        softCheck,
        pageRefresh: isRefreshLikeScenario,
        isLMSPath,
        wasViewingLMS,
        time: Date.now()
      });
      
      // Set a flag for successive checks
      if (isLMSPath) {
        sessionStorage.setItem('permissions_still_loading', 'true');
        
        // Create a cleanup timer to remove this flag
        setTimeout(() => {
          sessionStorage.removeItem('permissions_still_loading');
        }, 5000);
      }
      
      // Log detailed diagnostics if in development mode
      if (process.env.NODE_ENV !== 'production') {
        console.log('PermissionRoute: Permission check details:', {
          requiredPermission,
          hasPermission: hasRequiredPermission,
          redirectPaused,
          globalSubmissionActive,
          softCheck,
          isRefreshLikeScenario,
          isLMSPath,
          wasViewingLMS,
          location: location.pathname
        });
      }
      
      // Don't redirect, just render the children with limited permissions
      // This allows forms to handle permission issues gracefully and pages to load during refresh
      return <Outlet />;
    }
    
    // Clear the stored path to avoid redirect loops
    sessionStorage.removeItem('lastAttemptedProtectedRoute');
    
    console.log('PermissionRoute: User lacks permission, redirecting to', redirectTo);
    return <Navigate to={redirectTo} replace />;
  }
  
  // If all checks pass, render the child routes
  return <Outlet />;
};

export default PermissionRoute;